import { useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import styles from './Waveform.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/store';
import { setCursor } from 'src/reducers/commonSlice';
import {
  getGlobalPlayerInstance,
  secondsToTimestamp,
  setCurrentPlayerInstance
} from '../GlobalPlayer/Player';
import Play from 'src/assets/icons/Play';
import Pause from 'src/assets/icons/Pause';
import { utcToIST } from '@cp/src/utils/DateHelper';
import { logError } from '@cp/src/utils/ApiHelper';
import { sendCallRecordingAuditAndDownload } from './actions';
import { useParams } from 'react-router-dom';
import { IWaveform } from './interface';
import { getFormattedDate } from './utils';
import { DownloadIcon } from '@navi/web-ui/lib/icons';
import { Roles } from '@cp/src/pages/auth/constants/AuthConstants';
import { ID_PREFIX, PROCESSING_MESSAGE, ERROR_MESSAGE } from './const';
import Refresh from '@cp/src/assets/images/icons/Refresh';

let count = 0;

export const Waveform = ({ url, metaData }: IWaveform) => {
  const waveForm = useRef<any>();
  const timeout = useRef<NodeJS.Timeout>();
  const [isReady, setIsReady] = useState(false);
  const [reFetch, setReFetch] = useState(false);
  const [loadingOrError, setLoadingOrError] = useState('Loading ...');
  const id = useRef<string>(`${ID_PREFIX}waveform_${++count}`);
  const globalPlayerUrl = useSelector((state: RootState) => state?.common?.globalPlayer?.url);
  const secondsElapsedOfGlobalPlayer = useSelector(
    (state: RootState) => state?.common?.globalPlayer?.secondsElapsedOfGlobalPlayer || 0
  );
  const [secondsElapsed, setSecondsElapsed] = useState(
    url === globalPlayerUrl ? secondsElapsedOfGlobalPlayer : 0
  );
  const globalPlaying = useSelector(
    (state: RootState) => state?.common?.globalPlayer?.status === 'play'
  );

  const dispatch = useDispatch();
  const syncWithGlobalPlayer = (cursor?: number) => {
    setCurrentPlayerInstance(waveForm?.current);
    if (cursor !== -1) {
      dispatch(setCursor({ cursor, currentPlayerContainerId: id.current, url }));
    }
  };

  const addEvents = (waveformInstance: any) => {
    waveformInstance.on('ready', () => {
      setIsReady(true);
    });

    waveformInstance.on('seek', (cursor: number) => {
      syncWithGlobalPlayer(cursor);
    });
  };

  useEffect(() => {
    if (amIPlaying()) {
      setSecondsElapsed(secondsElapsedOfGlobalPlayer);
    }
  }, [secondsElapsedOfGlobalPlayer]);

  useEffect(() => {
    if (!waveForm?.current) {
      try {
        waveForm.current = WaveSurfer.create({
          barWidth: 1,
          barRadius: 1,
          barGap: 0.5,
          barMinHeight: 1,
          cursorWidth: 1,
          container: `#${id.current}`,
          backend: 'WebAudio',
          height: 40,
          progressColor: '#FE6E00',
          responsive: true,
          waveColor: '#C1C1C1',
          cursorColor: 'transparent'
        });

        addEvents(waveForm.current);
      } catch (error) {
        logError(error);
      }
    }
    return () => {
      waveForm?.current?.destroy();
    };
  }, []);

  useEffect(() => {
    if (waveForm?.current && url) {
      waveForm.current.load(url);
      if (url === globalPlayerUrl) {
        return syncWithGlobalPlayer(-1);
      }
    }
  }, [url, reFetch]);

  const handlePlayPause = () => {
    if (url !== globalPlayerUrl) {
      const cursor = waveForm.current.getCurrentTime() / waveForm.current.getDuration();
      return syncWithGlobalPlayer(cursor);
    }

    getGlobalPlayerInstance()?.playPause();
  };

  const amIPlaying = () => {
    return url == globalPlayerUrl && globalPlaying;
  };

  const shouldIBeVisible = () => {
    clearTimeout(timeout.current);
    const fifteenMinutesBeforeNow = new Date();
    fifteenMinutesBeforeNow.setMinutes(fifteenMinutesBeforeNow.getMinutes() - 15);
    const interactionCreatedAt = utcToIST(new Date(metaData?.interactionCreatedAt as string));
    timeout.current = setTimeout(
      () =>
        setLoadingOrError(
          interactionCreatedAt > fifteenMinutesBeforeNow ? PROCESSING_MESSAGE : ERROR_MESSAGE
        ),
      5e3
    );
    return waveForm.current?.getDuration();
  };

  const { loanId = '' } = useParams();

  const userReferenceId = useSelector(
    (state: RootState) => state?.common?.userData?.referenceId
  ) as string;
  const userRole = useSelector((state: RootState) => state?.common?.userData?.roles);
  const shouldIBeDownloaded = userRole?.includes(Roles.ROLE_GLOBAL_ACCESS);
  const interactionId = metaData?.interactionId?.toString() as string;
  const phoneNumber = metaData?.phoneNumber as string;
  const interactionCreatedAt = metaData?.interactionCreatedAt as string;
  const formattedDate = getFormattedDate(utcToIST(new Date(interactionCreatedAt)));
  const fileName = `${loanId}_${phoneNumber}_${metaData?.callType}_${formattedDate}.mp3`;

  const handleDownload = () => {
    sendCallRecordingAuditAndDownload(
      interactionId,
      phoneNumber,
      loanId,
      url,
      userReferenceId,
      fileName
    );
  };

  return (
    <div className={styles.wavesWrapper}>
      {
        <div className={styles.secondsElapsed} style={{ visibility: isReady ? 'visible' : 'none' }}>
          {shouldIBeVisible() ? (
            <>
              <span style={{ fontWeight: amIPlaying() ? 'bold' : '' }} className={styles.current}>
                {secondsToTimestamp(secondsElapsed)}
              </span>{' '}
              /{' '}
              <span className={styles.duration}>
                {secondsToTimestamp(waveForm?.current?.getDuration())}
              </span>
            </>
          ) : loadingOrError === ERROR_MESSAGE ? (
            'Unable to play'
          ) : loadingOrError === PROCESSING_MESSAGE ? (
            'Please wait...'
          ) : (
            'Loading ...'
          )}
        </div>
      }

      <div className={styles.secondRow}>
        {shouldIBeVisible() ? (
          <span onClick={handlePlayPause} className={styles.playPause}>
            {!amIPlaying() ? <Play /> : <Pause />}
          </span>
        ) : null}
        <div
          style={{
            pointerEvents: shouldIBeVisible() ? 'all' : 'none'
          }}
          className={styles.waves}
          id={id.current}
        ></div>
        {shouldIBeVisible() && shouldIBeDownloaded ? (
          <span onClick={handleDownload} className={styles.downloadButton}>
            {<DownloadIcon width={16} height={16} color="var(--navi-color-gray-c1)" />}
          </span>
        ) : null}
      </div>
      {!shouldIBeVisible() && (
        <>
          {loadingOrError === ERROR_MESSAGE && (
            <div className={styles.loadingOrError}>{loadingOrError}</div>
          )}
          {loadingOrError === PROCESSING_MESSAGE && (
            <div className={styles.processing}>
              {loadingOrError}
              <span onClick={() => setReFetch(prev => !prev)} className={styles.refreshIcon}>
                <Refresh />
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
};
